import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Dialog, Form, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { Button, type ButtonVariant } from './Button';
import { cx } from './cx';
import { Icon } from './Icon';
import { TextField } from './TextField';
import s from './ConfirmDialog.module.css';

export interface ConfirmSummaryRow {
  label: string;
  /** The value after the change. */
  value: string;
  /** The value before the change, shown struck through with an arrow. */
  before?: string;
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: string;
  /** One sentence on what will happen. */
  intro?: string;
  /** What changes, one row per value. */
  summary: readonly ConfirmSummaryRow[];
  /** Label of the reason field ("Why are you making this change?"). */
  reasonLabel?: string;
  /** A line under the reason field's label, read with it (who reads a note, APP-204). */
  reasonDescription?: string;
  /** What the reason field holds each time the dialog opens, for the person to keep or edit. */
  initialReason?: string;
  /** Every budget and settings change needs a reason; turn off only where the design omits it. */
  reasonRequired?: boolean;
  /** No reason field at all: a confirmation of figures already entered (Mark paid). */
  withoutReason?: boolean;
  confirmLabel: string;
  confirmVariant?: ButtonVariant;
  cancelLabel?: string;
  /**
   * Called with the reason. Return a promise to keep the dialog busy until it settles; resolve to
   * `false` to keep the dialog open (the change didn't go through and `error` says why).
   */
  onConfirm: (reason: string) => unknown;
  /** Why the last attempt didn't go through, shown in the dialog. */
  error?: string | null;
  /** More of the dialog after the summary (a choice of whom it's for). */
  before?: ReactNode;
  /**
   * Shown in place of the reason and the buttons, when what the person chose there brings its own
   * (a Stock Admin acting for someone: the step, APP-148).
   */
  takeover?: ReactNode;
  /**
   * The reason, when the caller keeps it (APP-148: one reason for "Just me" and the step). Left
   * out, the dialog keeps its own, starting from `initialReason` each time it opens.
   */
  reason?: string;
  onReasonChange?: (reason: string) => void;
  /** Work the takeover is doing: the dialog can't be dismissed until it settles. */
  busy?: boolean;
}

const REASON_MISSING = 'Add a short reason. It goes in the history with this change.';

/**
 * The servers trim a reason before checking there is one, so spaces alone count as none. A
 * `validate` result goes through the same native check as an empty field: the browser stops the
 * submit, and React Aria shows this error and moves focus to the field.
 */
function reasonGiven(value: string): string | null {
  return value.trim() === '' ? REASON_MISSING : null;
}

/** A confirm summary with a reason field, used before every budget or settings change. */
export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  intro,
  summary,
  reasonLabel = 'Reason',
  reasonDescription,
  initialReason = '',
  reasonRequired = true,
  withoutReason = false,
  confirmLabel,
  confirmVariant = 'navy',
  cancelLabel = 'Cancel',
  onConfirm,
  error = null,
  before,
  takeover,
  reason: kept,
  onReasonChange,
  busy = false,
}: ConfirmDialogProps): ReactElement {
  // Null until the person types: the field shows `initialReason`, which may change while closed.
  const [typed, setTyped] = useState<string | null>(null);
  const reason = kept ?? typed ?? initialReason;
  const setReason = (next: string): void => {
    if (onReasonChange === undefined) setTyped(next);
    else onReasonChange(next);
  };
  const [pending, setPending] = useState(false);
  const confirming = useRef(false);

  const close = (): void => {
    setTyped(null);
    onOpenChange(false);
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      isDismissable={!pending && !busy}
      isKeyboardDismissDisabled={pending || busy}
      className={s.overlay}
    >
      <Modal className={cx(s.modal, takeover !== undefined && s.modalWide)}>
        <Dialog role="alertdialog" className={s.dialog}>
          <Heading slot="title" className={cx('disp', s.title)}>
            {title}
          </Heading>
          {intro !== undefined && <p className={s.intro}>{intro}</p>}
          {summary.length > 0 && (
            <dl className={s.summary}>
              {summary.map((row) => (
                <div key={row.label} className={s.row}>
                  <dt>{row.label}</dt>
                  <dd>
                    {row.before !== undefined && (
                      <>
                        <span className={s.before}>{row.before}</span>
                        <span className={s.arrow}>
                          <Icon name="arrowRight" size={14} />
                        </span>
                        <span className="visually-hidden">changes to</span>
                      </>
                    )}
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {before}
          {takeover ?? (
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                // Form submissions can recur before the pending state renders.
                if (confirming.current) return;
                confirming.current = true;
                setPending(true);
                void Promise.resolve()
                  .then(() => onConfirm(reason.trim()))
                  .then((done) => {
                    if (done !== false) close();
                  })
                  .finally(() => {
                    confirming.current = false;
                    setPending(false);
                  });
              }}
            >
              <div className={s.formBody}>
                {/* React Aria's pending announcement otherwise retains the unmounted button's ID. */}
                <p className="visually-hidden" role="status" aria-live="polite">
                  {pending ? `${confirmLabel} in progress.` : ''}
                </p>
                {error !== null && (
                  <p className={s.error} role="alert">
                    {error}
                  </p>
                )}
                {!withoutReason && (
                  <TextField
                    label={reasonLabel}
                    {...(reasonDescription !== undefined && { description: reasonDescription })}
                    multiline
                    value={reason}
                    onChange={setReason}
                    isRequired={reasonRequired}
                    {...(reasonRequired && { validate: reasonGiven })}
                    errorMessage={REASON_MISSING}
                  />
                )}
                <div className={s.actions}>
                  <Button variant="ghost" onPress={close} isDisabled={pending}>
                    {cancelLabel}
                  </Button>
                  <Button
                    type={pending ? 'button' : 'submit'}
                    variant={confirmVariant}
                    aria-disabled={pending}
                    aria-busy={pending}
                    data-pending={pending || undefined}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
